import { CanonicalBusinessEvent } from "../types/cbes";

export interface CbeComparisonGroup {
  value: string;
  label: string;
  usecaseName: string;
  idempotenceId?: string;
}

const keyFor = (usecaseName: string, idempotenceId?: string): string =>
  idempotenceId === undefined
    ? usecaseName
    : `${usecaseName}::${encodeURIComponent(idempotenceId)}`;

const getIdempotenceSuffix = (
  idempotenceId: string,
  usecaseName: string
): string => {
  const usecasePrefix = `.${usecaseName}.`;
  const usecaseIndex = idempotenceId.indexOf(usecasePrefix);
  const idAfterUsecase =
    usecaseIndex === -1
      ? idempotenceId
      : idempotenceId.slice(usecaseIndex + usecasePrefix.length);
  const dotSeparatorIndex = idAfterUsecase.indexOf(".");

  return dotSeparatorIndex === -1
    ? ""
    : idAfterUsecase.slice(dotSeparatorIndex + 1);
};

export const createCbeComparisonGroups = (
  cbes: CanonicalBusinessEvent[]
): CbeComparisonGroup[] => {
  const suffixesByUsecase = new Map<string, Map<string, string>>();

  cbes.forEach((cbe) => {
    const usecaseName = cbe.usecaseName || "Unknown";
    const suffix = getIdempotenceSuffix(cbe.idempotenceId, usecaseName);
    const idsBySuffix =
      suffixesByUsecase.get(usecaseName) || new Map<string, string>();
    if (!idsBySuffix.has(suffix)) {
      idsBySuffix.set(suffix, cbe.idempotenceId);
    }
    suffixesByUsecase.set(usecaseName, idsBySuffix);
  });

  const groups: CbeComparisonGroup[] = [];
  suffixesByUsecase.forEach((idsBySuffix, usecaseName) => {
    if (idsBySuffix.size === 1) {
      groups.push({
        value: keyFor(usecaseName),
        label: usecaseName,
        usecaseName,
      });
      return;
    }

    idsBySuffix.forEach((idempotenceId, suffix) => {
      groups.push({
        value: keyFor(usecaseName, idempotenceId),
        label: suffix ? `${usecaseName} · ${suffix}` : usecaseName,
        usecaseName,
        idempotenceId,
      });
    });
  });

  return groups;
};

export const filterCbesByComparisonGroup = (
  cbes: CanonicalBusinessEvent[],
  group: CbeComparisonGroup,
  destination: string
): CanonicalBusinessEvent[] =>
  cbes.filter(
    (cbe) =>
      cbe.usecaseName === group.usecaseName &&
      cbe.destination === destination &&
      (group.idempotenceId === undefined ||
        cbe.idempotenceId === group.idempotenceId)
  );
